/**
 * The start sheet (decision 0013): the game opens on it over the world. Today's expedition and
 * endless as two option cards, with today's state deciding which is chosen and what the button
 * says. A bottom sheet on a phone, a centred card on desktop.
 */
import type { StringKey } from "../i18n.ts";
import { betaBand } from "./beta.ts";
import { esc, icon, logo } from "./dom.ts";

type T = (key: StringKey, params?: Record<string, string | number>) => string;

export type TodayState = "new" | "progress" | "done";

export interface StartState {
  readonly today: TodayState;
  readonly expedition: number;
  /** Today's distance so far, or of the attempt when done, already formatted. */
  readonly todayDistance: string;
  readonly dateLong: string;
  readonly dateShort: string;
  /** Until the next expedition, formatted. */
  readonly nextIn: string;
  /** The endless best, formatted, or null before the first run. */
  readonly endlessBest: string | null;
}

export function startHtml(t: T, web: boolean, beta: boolean): string {
  const option = (action: string, title: string, tag: string, line: string) => `
    <button type="button" class="option" data-action="${action}" aria-pressed="false">
      <span class="radio" aria-hidden="true"></span>
      <span class="option-body">
        <span class="option-head"><span class="title">${esc(title)}</span><span class="kicker" data-bind="${tag}"></span></span>
        <span class="option-line" data-bind="${line}"></span>
      </span>
    </button>`;
  return `
  <div class="start">
    <button type="button" class="float-menu" data-action="menu" aria-label="${esc(t("hud.menu"))}">${icon("menu")}</button>
    <div class="start-card" role="dialog" aria-label="${esc(t("app.title"))}">
      ${beta ? betaBand(t, "beta") : ""}
      <div class="brand">${logo("mist", t("app.title"))}<div class="kicker tagline">${esc(t("app.tagline"))}</div></div>
      <div class="options">
        ${option("pickDaily", t("button.daily"), "startDailyTag", "startDailyLine")}
        ${option("pickEndless", t("button.endless"), "startEndlessTag", "startEndlessLine")}
      </div>
      <button type="button" class="primary set-out" data-action="setOut" data-bind="setOutLabel"></button>
      <div class="links">
        <button type="button" class="link" data-action="howto">${esc(t("menu.howto"))}</button>
        ${web ? `<button type="button" class="link" data-action="getApp">${esc(t("menu.getApp"))}</button>` : ""}
      </div>
    </div>
  </div>`;
}

/** The texts of the start sheet for a state, the option chosen, and the layout. */
export function startTexts(
  t: T,
  s: StartState,
  pick: "daily" | "endless",
  desk: boolean,
): Record<string, string> {
  const n = s.expedition;
  let tag: string;
  let line: string;
  if (s.today === "new") {
    tag = desk ? t("start.tagNewDate", { n, dateShort: s.dateShort }) : t("start.tagNew", { n });
    line = desk ? t("start.todayNewShort") : t("start.todayNew", { dateLong: s.dateLong });
  } else if (s.today === "progress") {
    tag = desk
      ? t("start.tagProgress", { n, distance: s.todayDistance })
      : t("start.tagDistance", { n, distance: s.todayDistance });
    line = desk ? t("start.todayInProgressShort") : t("start.todayInProgress");
  } else {
    tag = desk
      ? t("start.tagDone", { n, distance: s.todayDistance })
      : t("start.tagDistance", { n, distance: s.todayDistance });
    line = desk
      ? t("start.todayDoneShort", { time: s.nextIn })
      : t("start.todayDone", { time: s.nextIn });
  }
  let setOut = t("start.setOut");
  if (pick === "daily" && s.today === "progress")
    setOut = t("start.continue", { distance: s.todayDistance });
  if (pick === "daily" && s.today === "done") setOut = t("button.practice");
  return {
    startDailyTag: tag,
    startDailyLine: line,
    startEndlessTag: s.endlessBest === null ? "" : t("hud.best", { distance: s.endlessBest }),
    startEndlessLine: t("start.endlessLine"),
    setOutLabel: setOut,
  };
}
